import { DailyAgendaActionFactory, DailyAgendaActions } from '../actions';
import {
  isSlotLocked,
  isSlotNoBookFlow,
  isSlotWithOpenWaitingList,
} from '../../../../utils/slotAvailability/slotAvailability';
import { navigateToBookingsServicePage } from '@wix/bookings-catalog-calendar-viewer-navigation';
import { AsyncComponentDataStatus, DailyAgendaState } from '../../state/types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import {
  BookingsQueryParams,
  getCurrentPath,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { navigateToBookingsFormPage } from '@wix/bookings-booking-navigation';
import { DialogType } from '../../viewModel/dialogViewModel/dialogViewModel';
import { REFERRAL_INFO } from '../../../../utils/bi/consts';

const DAILY_AGENDA_REFFERAL = REFERRAL_INFO;

export const createOnSlotClickAction: DailyAgendaActionFactory<
  DailyAgendaActions['onSlotClick']
> =
  ({ getState, flowAPI, setState, biLogger }) =>
  async (slotId) => {
    const { slots, isPremium, services } = getState();
    if (slots.status !== AsyncComponentDataStatus.IDLE) {
      return;
    }
    const slot = slots.data.find(
      (timeSlot) => timeSlot.eventInfo?.eventId === slotId,
    );

    if (!slot) {
      console.error(`Slot with id ${slotId} not found`);
      return;
    }

    biLogger.reportInitialIntentToBook(
      (services.data as { [key: string]: Service })[slot.serviceId!].type!,
      slot.serviceId!,
      isPremium,
    );

    if (!isPremium) {
      setState({
        dialog: {
          type: flowAPI.environment.isViewer
            ? DialogType.PremiumViewer
            : DialogType.PremiumPreview,
        },
      });
      return;
    }

    if (isSlotNoBookFlow(slot)) {
      handleNoBookFlow({ slot, state: getState(), flowAPI });
      return;
    }

    if (isSlotWithOpenWaitingList(slot) || isSlotLocked(slot)) {
      setState({ dialog: { type: DialogType.JoinWaitlist } });
      return;
    }

    handleRegularBookFlow({
      flowAPI,
      state: getState(),
      slot,
    });
    biLogger.reportSlotSelectionCompleted(
      (services.data as { [key: string]: Service })[slot.serviceId!].type!,
      slot.serviceId!,
      new Date(slot.localStartDate!),
      new Date(slot.localEndDate!),
      isPremium,
    );
  };

const handleNoBookFlow = ({
  state: { services },
  flowAPI: {
    controllerConfig: { wixCodeApi },
  },
  slot,
}: {
  slot: TimeSlot;
  state: DailyAgendaState;
  flowAPI: ControllerFlowAPI;
}) => {
  const serviceSlug = (services.data as { [key: string]: Service })[
    slot.serviceId!
  ].mainSlug?.name!;

  navigateToBookingsServicePage(
    wixCodeApi,
    {
      serviceSlug,
      referral: DAILY_AGENDA_REFFERAL,
    },
    true,
  );
};

const handleRegularBookFlow = async ({
  flowAPI,
  state,
  slot,
}: {
  state: DailyAgendaState;
  flowAPI: ControllerFlowAPI;
  slot: TimeSlot;
}) => {
  const isCartEnabled = true; // assuming cart is enabled for now TODO - get active features;
  const { selectedTimezone, services } = state;
  try {
    const serviceSlug = (services.data as { [key: string]: Service })[
      slot.serviceId!
    ].mainSlug?.name!;
    const continueShoppingUrl = isCartEnabled
      ? `/${getCurrentPath(flowAPI.controllerConfig.wixCodeApi).join('/')}?${
          BookingsQueryParams.TIMEZONE
        }=${state.selectedTimezone}`
      : undefined;
    void navigateToBookingsFormPage({
      wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
      platformAPIs: flowAPI.controllerConfig.platformAPIs,
      timeSlot: slot,
      serviceSlug,
      queryParams: {
        [BookingsQueryParams.REFERRAL]: DAILY_AGENDA_REFFERAL,
        [BookingsQueryParams.TIMEZONE]: selectedTimezone!,
        ...(isCartEnabled
          ? {
              [BookingsQueryParams.CONTINUE_SHOPPING_URL]: continueShoppingUrl,
            }
          : {}),
      },
    });
  } catch (e) {
    console.error('Error navigating to bookings form page', e);
  }
};
